import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  InlineStack,
  Text,
  Banner,
  Box,
  BlockStack,
  Spinner,
  Toast,
  Badge,
} from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getVehicle,
  updateVehicle,
  createVehicle,
  deleteVehicle,
  type Vehicle,
  type VehicleUpdate,
} from '../api/vehicles';
import { MatchSourceBadge } from '../components/ConfidenceBadge';
import { BoltPatternEditor } from '../components/BoltPatternEditor';
import { AuditLogPanel } from '../components/AuditLogPanel';
import { recomputeMatches, getVehicleMatches } from '../api/matches';

type Mode = 'create' | 'edit';

export function VehicleDetail({ mode }: { mode: Mode }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const numericId = id ? parseInt(id, 10) : null;

  const { data: vehicle, isLoading, error } = useQuery({
    queryKey: ['vehicle', numericId],
    queryFn: () => getVehicle(numericId!),
    enabled: mode === 'edit' && numericId !== null,
  });

  // Lightweight matches summary on this page so the YMM tag count is visible
  // at a glance. Full table lives at /fitment-debug/:id (linked below).
  const matchesQuery = useQuery({
    queryKey: ['vehicle-matches-summary', numericId],
    queryFn: () => getVehicleMatches(numericId!),
    enabled: mode === 'edit' && numericId !== null,
    staleTime: 30_000,
  });

  const [form, setForm] = useState<Partial<Vehicle>>({});
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (vehicle) setForm(vehicle);
  }, [vehicle]);

  const save = useMutation({
    mutationFn: async (patch: VehicleUpdate) => {
      if (mode === 'edit' && numericId) {
        return updateVehicle(numericId, patch);
      }
      return createVehicle(patch as any);
    },
    onSuccess: (v) => {
      setToast(mode === 'edit' ? 'Saved' : 'Created');
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      if (mode === 'create') navigate(`/vehicles/${v.id}`, { replace: true });
    },
  });

  const del = useMutation({
    mutationFn: () => deleteVehicle(numericId!),
    onSuccess: () => {
      setToast('Archived');
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      navigate('/vehicles');
    },
  });

  const recompute = useMutation({
    mutationFn: () => recomputeMatches({ scope: 'vehicle', id: numericId! }),
    onSuccess: (res) => {
      setToast(`Matches recomputed (inserted ${res.inserted ?? 0}, deleted ${res.deleted ?? 0})`);
      qc.invalidateQueries({ queryKey: ['matches', numericId] });
    },
    onError: (err: Error) => setToast(`Recompute failed: ${err.message}`),
  });

  if (mode === 'edit' && isLoading) {
    return <Page title="Vehicle"><Spinner /></Page>;
  }
  if (mode === 'edit' && error) {
    return (
      <Page title="Vehicle">
        <Banner tone="critical" title="Failed to load">{(error as Error).message}</Banner>
      </Page>
    );
  }

  const v = form as Vehicle;
  const handle = <K extends keyof Vehicle>(key: K) => (value: Vehicle[K] | string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const handleNum = <K extends keyof Vehicle>(key: K) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value === '' ? null : Number(value) } as Partial<Vehicle>));
  };

  return (
    <>
      <Page
        title={mode === 'create' ? 'New vehicle' : `${v.year} ${v.make} ${v.model}`}
        subtitle={mode === 'edit' && v.submodel ? v.submodel : undefined}
        backAction={{ content: 'Vehicles', onAction: () => navigate('/vehicles') }}
        primaryAction={{
          content: 'Save changes',
          loading: save.isPending,
          onAction: () => save.mutate(buildPatch(form)),
        }}
        secondaryActions={mode === 'edit' ? [
          { content: 'View matches', onAction: () => navigate(`/fitment-debug/${numericId}`) },
          { content: 'Recompute matches', loading: recompute.isPending,
            helpText: 'Re-run matching for this vehicle',
            onAction: () => recompute.mutate() },
          { content: 'Archive', destructive: true, loading: del.isPending,
            onAction: () => { if (confirm('Archive this vehicle?')) del.mutate(); } },
        ] : []}
      >
        {save.error && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical" title="Save failed">{(save.error as Error).message}</Banner>
          </Box>
        )}
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Identity</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField label="Year" type="number" value={String(v.year ?? '')} onChange={handleNum('year')} autoComplete="off" />
                    <TextField label="Make" value={v.make ?? ''} onChange={handle('make')} autoComplete="off" helpText="Canonicalized on save" />
                    <TextField label="Model" value={v.model ?? ''} onChange={handle('model')} autoComplete="off" />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField label="Submodel" value={v.submodel ?? ''} onChange={handle('submodel')} autoComplete="off" />
                    <Select
                      label="Drive type"
                      options={[
                        { label: '—', value: '' },
                        { label: '2WD', value: '2WD' }, { label: '4WD', value: '4WD' },
                        { label: 'AWD', value: 'AWD' }, { label: 'RWD', value: 'RWD' },
                        { label: 'FWD', value: 'FWD' },
                      ]}
                      value={v.drive_type ?? ''}
                      onChange={handle('drive_type')}
                    />
                    <TextField label="Body config" value={v.body_config ?? ''} onChange={handle('body_config')} autoComplete="off" helpText="SRW | DRW | CrewCab | …" />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">Bolt pattern</Text>
                  {mode === 'edit' && (
                    <MatchSourceBadge source={v.bolt_pattern_match_source} />
                  )}
                </InlineStack>
                <BoltPatternEditor
                  value={v.bolt_pattern_raw ?? ''}
                  onChange={handle('bolt_pattern_raw')}
                  singleOnly
                  helpText="One canonical per vehicle. Live preview below."
                />
                {mode === 'edit' && (
                  <FormLayout>
                    <FormLayout.Group>
                      <TextField label="Normalized (stored)" value={v.bolt_pattern_normalized ?? ''} disabled autoComplete="off" />
                      <TextField label="Canonical (stored)" value={v.bolt_pattern_canonical ?? ''} disabled autoComplete="off" />
                      <TextField label="Thread pitch" value={v.thread_pitch ?? ''} onChange={handle('thread_pitch')} autoComplete="off" />
                    </FormLayout.Group>
                  </FormLayout>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Fitment envelope</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField label="Hub bore front (mm)" type="number" step={0.1} value={numStr(v.hub_bore_front_mm)} onChange={handleNum('hub_bore_front_mm')} autoComplete="off" />
                    <TextField label="Hub bore rear (mm)" type="number" step={0.1} value={numStr(v.hub_bore_rear_mm)} onChange={handleNum('hub_bore_rear_mm')} autoComplete="off" />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField label="Diameter min (in)" type="number" step={0.1} value={numStr(v.wheel_diameter_min_in)} onChange={handleNum('wheel_diameter_min_in')} autoComplete="off" />
                    <TextField label="Diameter max (in)" type="number" step={0.1} value={numStr(v.wheel_diameter_max_in)} onChange={handleNum('wheel_diameter_max_in')} autoComplete="off" />
                    <TextField label="Front width max (in)" type="number" step={0.1} value={numStr(v.front_width_max_in)} onChange={handleNum('front_width_max_in')} autoComplete="off" />
                    <TextField label="Rear width max (in)" type="number" step={0.1} value={numStr(v.rear_width_max_in)} onChange={handleNum('rear_width_max_in')} autoComplete="off" />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField label="Front offset min" type="number" value={numStr(v.front_offset_min_mm)} onChange={handleNum('front_offset_min_mm')} autoComplete="off" />
                    <TextField label="Front offset max" type="number" value={numStr(v.front_offset_max_mm)} onChange={handleNum('front_offset_max_mm')} autoComplete="off" />
                    <TextField label="Rear offset min" type="number" value={numStr(v.rear_offset_min_mm)} onChange={handleNum('rear_offset_min_mm')} autoComplete="off" />
                    <TextField label="Rear offset max" type="number" value={numStr(v.rear_offset_max_mm)} onChange={handleNum('rear_offset_max_mm')} autoComplete="off" />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Notes + TPMS</Text>
                <FormLayout>
                  <TextField label="TPMS sensor" value={v.tpms_sensor ?? ''} onChange={handle('tpms_sensor')} autoComplete="off" />
                  <TextField label="Fitment notes" multiline={4} value={v.fitment_notes ?? ''} onChange={handle('fitment_notes')} autoComplete="off" />
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          {mode === 'edit' && numericId && (
            <Layout.Section>
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h3" variant="headingMd">Matched wheels</Text>
                    <InlineStack gap="200">
                      <Badge tone={matchesQuery.data ? (matchesQuery.data.data.length > 0 ? 'success' : 'attention') : 'info'}>
                        {matchesQuery.isLoading ? 'Loading…' : `${matchesQuery.data?.data.length ?? 0} matches`}
                      </Badge>
                      <Button onClick={() => navigate(`/fitment-debug/${numericId}`)} variant="plain">
                        View full match table
                      </Button>
                    </InlineStack>
                  </InlineStack>
                  {matchesQuery.data && matchesQuery.data.data.length > 0 && (
                    <Box>
                      {(() => {
                        const groups: Record<string, number> = {};
                        for (const m of matchesQuery.data.data) {
                          const key = (m.confidence as string) || 'unknown';
                          groups[key] = (groups[key] ?? 0) + 1;
                        }
                        return (
                          <InlineStack gap="200" wrap>
                            {Object.entries(groups).map(([k, n]) => (
                              <Badge key={k} tone={k === 'exact' ? 'success' : k === 'alias' ? 'info' : 'attention'}>
                                {`${n} ${k}`}
                              </Badge>
                            ))}
                          </InlineStack>
                        );
                      })()}
                    </Box>
                  )}
                  {matchesQuery.data && matchesQuery.data.data.length === 0 && !matchesQuery.isLoading && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      No matches yet. Click "Recompute matches" above to rebuild this vehicle's fitment, or check the bolt-pattern + offset specs are populated.
                    </Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          )}

          {mode === 'edit' && numericId && (
            <Layout.Section>
              <AuditLogPanel entityTable="vehicles" entityId={numericId} />
            </Layout.Section>
          )}
        </Layout>
        {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
      </Page>
    </>
  );
}

function numStr(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n);
}

// Whitelist of editable fields sent to PATCH /admin/vehicles/:id.
// Anything not listed here is either server-computed (make_slug, canonical BP)
// or immutable (id, created_at). Using a whitelist (rather than the old
// "strip everything we know about" approach) keeps the UI resilient to the
// API adding new response fields.
const EDITABLE_FIELDS = [
  'year', 'make', 'model', 'submodel', 'drive_type', 'body_config',
  'bolt_pattern_raw', 'thread_pitch',
  'hub_bore_front_mm', 'hub_bore_rear_mm',
  'front_offset_min_mm', 'front_offset_max_mm',
  'rear_offset_min_mm', 'rear_offset_max_mm',
  'wheel_diameter_min_in', 'wheel_diameter_max_in',
  'front_width_max_in', 'rear_width_max_in',
  'oem_tire_sizes_front', 'oem_tire_sizes_rear', 'plus_size_recommendations',
  'tpms_sensor', 'fitment_notes',
] as const;

// Fields where an empty-string UI value must be translated to null for the
// API — the Select component emits '' for the "—" option, but the Zod schema
// on the server only accepts an enum value or null.
const NULL_IF_EMPTY = new Set<string>(['drive_type', 'submodel', 'body_config', 'thread_pitch', 'tpms_sensor', 'fitment_notes']);

function buildPatch(form: Partial<Vehicle>): VehicleUpdate {
  const out: Record<string, unknown> = {};
  for (const k of EDITABLE_FIELDS) {
    const v = (form as any)[k];
    if (v === undefined) continue;
    out[k] = v === '' && NULL_IF_EMPTY.has(k) ? null : v;
  }
  return out as VehicleUpdate;
}
